// ConvoCX unified inbox — login gate (CON-845, login surface CON-853 v2).
// Sign-in + sign-up on one card: SSO (Google, stub contract), email/password,
// "remember me" (save login), forgot-password notice, and an account creation
// toggle wired to the backend /auth/register.

import React, { useState } from 'react';
import {
  Button,
  Card,
  Center,
  CheckboxInput,
  Divider,
  Heading,
  Text,
  TextInput,
  VStack,
} from '@astryxdesign/core';
import { useAuth } from './auth';
import { api } from './api';
import { ChatBubbleLogo, CONVOX_WORDMARK } from '../brand/Logo';

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export function LoginGate() {
  const { login, register, loading, error } = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [accountName, setAccountName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [ssoNotice, setSsoNotice] = useState<string | null>(null);
  const [forgotNotice, setForgotNotice] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'signup') {
      if (!accountName || !email || !password) return;
      await register({ accountName: accountName.trim(), email: email.trim(), password, displayName: accountName.trim() });
    } else {
      if (!email || !password) return;
      await login(email.trim(), password, remember);
    }
  };

  const handleGoogle = async () => {
    setSsoNotice(null);
    try {
      const meta = await api.ssoStart('google');
      setSsoNotice(meta.configured ? `Redirecting to ${meta.authUrl}…` : 'Google SSO is being set up — use email sign-in for now.');
    } catch {
      setSsoNotice('Google SSO is not ready yet — use email sign-in for now.');
    }
  };

  return (
    <main>
      <Center minHeight="100vh" width="100%" padding={4}>
        <Card style={{ width: '100%', maxWidth: 400, padding: 'var(--space-8)' }}>
          <VStack gap={6} align="center" style={{ textAlign: 'center' }}>
            <ChatBubbleLogo size={56} />
            {CONVOX_WORDMARK}
            <Heading level={1}>{mode === 'signin' ? 'Sign in to your inbox' : 'Create your account'}</Heading>
            <Text type="supporting" color="secondary">
              All your conversations, one unified inbox.
            </Text>
          </VStack>

          <VStack gap={3} style={{ marginTop: 'var(--space-6)' }}>
            <Button
              label="Continue with Google"
              variant="secondary"
              size="md"
              width="fill"
              icon={<GoogleMark />}
              isDisabled={loading}
              onClick={() => void handleGoogle()}
            />
            {ssoNotice && (
              <Text type="supporting" role="status" style={{ textAlign: 'center' }}>
                {ssoNotice}
              </Text>
            )}
            <Divider label="or" />
          </VStack>

          <form onSubmit={submit} noValidate>
            <VStack gap={4} style={{ marginTop: 'var(--space-2)' }}>
              {mode === 'signup' && (
                <TextInput
                  label="Workspace / company name"
                  value={accountName}
                  onChange={setAccountName}
                  placeholder="Acme Inc."
                  autoComplete="organization"
                  isRequired
                />
              )}
              <TextInput
                label="Email address"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@company.com"
                autoComplete="email"
                isRequired
              />
              <TextInput
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                isRequired
              />

              {mode === 'signin' && (
                <div className="ccx-login__row">
                  <CheckboxInput
                    label="Save login on this device"
                    value={remember}
                    onChange={setRemember}
                  />
                  <button
                    type="button"
                    className="ccx-login__link"
                    onClick={() => {
                      setForgotNotice(true);
                      setSsoNotice(null);
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              {forgotNotice && (
                <Text type="supporting" role="status">
                  Password reset is on the roadmap — contact your workspace admin or sign in with the account email for now.
                </Text>
              )}

              {error && (
                <Text type="supporting" role="alert" style={{ color: 'var(--color-error)' }}>
                  {error}
                </Text>
              )}

              <Button
                label={loading ? (mode === 'signup' ? 'Creating account…' : 'Signing in…') : mode === 'signup' ? 'Create account' : 'Sign in'}
                variant="primary"
                size="md"
                isDisabled={loading}
                width="fill"
                type="submit"
              />
            </VStack>
          </form>

          <VStack gap={2} align="center" style={{ marginTop: 'var(--space-5)' }}>
            <Text type="supporting" color="secondary">
              {mode === 'signin' ? 'New to ConvoCX?' : 'Already have an account?'}
            </Text>
            <Button
              label={mode === 'signin' ? 'Create an account' : 'Back to sign in'}
              variant="ghost"
              size="sm"
              onClick={() => setMode((m) => (m === 'signin' ? 'signup' : 'signin'))}
            />
          </VStack>
        </Card>
      </Center>
    </main>
  );
}